import React from 'react';
import { MdOutlineSchool } from 'react-icons/md';
import { SectionCard, Pill, Note, Disclosure } from './ui';
import { fmtMiles, ratingTone, safeArray, isNum } from '../format';

const LEVELS = [
  { key: 'elementary', label: 'Elementary' },
  { key: 'middle', label: 'Middle' },
  { key: 'high', label: 'High' },
];

function SchoolRow({ school, nearest }) {
  const rating = school.rating || {};
  const tone = ratingTone(rating.normalized_value);
  const grade = rating.raw_value && rating.raw_value !== 'N/A' ? rating.raw_value : null;
  const span = school.grade_span && (school.grade_span.low || school.grade_span.high)
    ? `${school.grade_span.low || '?'}–${school.grade_span.high || '?'}`
    : null;
  return (
    <li className="le-school">
      <span className={`le-school-grade le-school-grade-${tone}`} title={isNum(rating.normalized_value) ? `${rating.normalized_value} on a 0–4.3 scale` : 'No rating'}>
        {grade || '–'}
      </span>
      <span className="le-school-body">
        <span className="le-school-name">{school.name}{nearest ? <Pill tone="warning" className="le-school-tag">Nearest</Pill> : null}</span>
        <span className="le-school-meta">
          {[span ? `Grades ${span}` : null, school.school_type || null, isNum(school.distance_miles) ? fmtMiles(school.distance_miles) : null].filter(Boolean).join(' · ')}
        </span>
      </span>
    </li>
  );
}

export default function SchoolsCard({ data, status, listingSchools }) {
  const assigned = data?.assigned_or_containing || {};
  const fallback = data?.nearest_fallback || {};
  const district = data?.district || {};
  const other = safeArray(assigned.other);
  const districtNames = [district.name, district.elementary_district_name, district.middle_district_name, district.high_district_name].filter(Boolean);
  const listed = safeArray(listingSchools);
  const anyAssigned = LEVELS.some((l) => safeArray(assigned[l.key]).length > 0);

  return (
    <SectionCard id="le-section-schools" icon={<MdOutlineSchool size={20} />} title="Schools" subtitle={data ? 'Attendance boundaries that contain this point · letter ratings' : null} status={status} dataset={data}>
      {data ? (
        <>
          {fallback.used ? (
            <Note tone="warning">No attendance boundary contains this point; the nearest schools are shown and labeled as such.</Note>
          ) : null}
          {districtNames.length ? (
            <div className="le-district">
              <strong>District:</strong> {Array.from(new Set(districtNames)).join(' · ')}
              {district.is_spatially_verified === false ? <span className="muted"> (listing-supplied, not spatially verified)</span> : null}
            </div>
          ) : null}

          <div className="le-school-levels">
            {LEVELS.map((level) => {
              const contained = safeArray(assigned[level.key]);
              const nearest = contained.length === 0 && fallback.used ? safeArray(fallback[level.key]) : [];
              const list = contained.length ? contained : nearest;
              return (
                <div key={level.key} className="le-school-level">
                  <div className="le-school-level-title">{level.label}</div>
                  {list.length ? (
                    <ul className="le-school-list">
                      {list.map((s) => <SchoolRow key={s.school_id || s.name} school={s} nearest={contained.length === 0} />)}
                    </ul>
                  ) : (
                    <div className="le-school-none">None in boundary</div>
                  )}
                </div>
              );
            })}
          </div>

          {!anyAssigned && !fallback.used ? <Note tone="muted">No school attendance boundaries are indexed for this location yet.</Note> : null}

          {other.length ? (
            <Disclosure summary={`${other.length} other school${other.length === 1 ? '' : 's'} in boundary (pre-K, special programs)`}>
              <ul className="le-school-list">{other.map((s) => <SchoolRow key={s.school_id || s.name} school={s} />)}</ul>
            </Disclosure>
          ) : null}

          {listed.length ? (
            <Disclosure summary={`Schools named in listing records (${listed.length})`}>
              <ul className="le-plain-list">
                {listed.map((s, i) => (
                  <li key={`${s.name}-${i}`}>
                    <strong>{s.name}</strong>
                    <span className="muted"> · {[s.level, s.grades, isNum(s.rating_out_of_10) ? `${s.rating_out_of_10}/10` : null, isNum(s.distance_miles) ? fmtMiles(s.distance_miles) : null, s.type].filter(Boolean).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </Disclosure>
          ) : null}
        </>
      ) : null}
    </SectionCard>
  );
}
